import axios from 'axios';

// Create an Axios instance with default configuration
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '', // Use environment variable or empty string
});

// Add a request interceptor to include the auth token in all requests
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

class PredictionService {
  /**
   * Get prediction for a specific inventory item
   * @param {string} itemId - ID of the inventory item
   * @returns {Promise} - Promise resolving to prediction data
   */
  static async getItemPrediction(itemId) {
    try {
      const response = await API.get(`/api/inventory/${itemId}/prediction`);
      return response.data;
    } catch (error) {
      console.error('Error getting item prediction:', error);
      throw error;
    }
  }

  /**
   * Get predictions for all inventory items
   * @returns {Promise} - Promise resolving to all predictions
   */
  static async getAllPredictions() {
    try {
      const response = await API.get('/api/inventory/predictions');
      return response.data;
    } catch (error) {
      console.error('Error getting all predictions:', error);
      throw error;
    }
  }

  /**
   * Generate a smart shopping list
   * @param {number} days - Days threshold for including items in the shopping list
   * @returns {Promise} - Promise resolving to shopping list data
   */
  static async getShoppingList(days = 7) {
    try {
      const response = await API.get(`/api/shopping-list?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error generating shopping list:', error);
      throw error;
    }
  }

  /**
   * Record consumption for an item
   * @param {string} itemId - ID of the inventory item
   * @param {number} newQuantity - New quantity after consumption
   * @param {string} notes - Optional notes about the consumption
   * @returns {Promise} - Promise resolving to consumption record data
   */
  static async recordConsumption(itemId, newQuantity, notes = null) {
    try {
      const response = await API.post(`/api/inventory/${itemId}/consume`, {
        new_quantity: newQuantity,
        notes
      });
      return response.data;
    } catch (error) {
      console.error('Error recording consumption:', error);
      throw error;
    }
  }

  /**
   * Record purchase for an item
   * @param {string} itemId - ID of the inventory item
   * @param {number} quantityAdded - Quantity added from purchase
   * @param {number} price - Optional price paid
   * @param {string} source - Optional source of purchase
   * @param {string} notes - Optional notes about the purchase
   * @returns {Promise} - Promise resolving to purchase record data
   */
  static async recordPurchase(itemId, quantityAdded, price = null, source = null, notes = null) {
    try {
      const response = await API.post(`/api/inventory/${itemId}/purchase`, {
        quantity_added: quantityAdded,
        price,
        source,
        notes
      });
      return response.data;
    } catch (error) {
      console.error('Error recording purchase:', error);
      throw error;
    }
  }

  /**
   * Get consumption history for an item
   * @param {string} itemId - ID of the inventory item
   * @returns {Promise} - Promise resolving to consumption history data
   */
  static async getConsumptionHistory(itemId) {
    try {
      const response = await API.get(`/api/inventory/${itemId}/consumption-history`);
      return response.data;
    } catch (error) {
      console.error('Error getting consumption history:', error);
      throw error;
    }
  }

  /**
   * Get purchase history for an item
   * @param {string} itemId - ID of the inventory item
   * @returns {Promise} - Promise resolving to purchase history data
   */
  static async getPurchaseHistory(itemId) {
    try {
      const response = await API.get(`/api/inventory/${itemId}/purchase-history`);
      return response.data;
    } catch (error) {
      console.error('Error getting purchase history:', error);
      throw error;
    }
  }

  /**
   * Get usage pattern for an item
   * @param {string} itemId - ID of the inventory item
   * @returns {Promise} - Promise resolving to usage pattern data
   */
  static async getUsagePattern(itemId) {
    try {
      const response = await API.get(`/api/inventory/${itemId}/usage-pattern`);
      return response.data;
    } catch (error) {
      console.error('Error getting usage pattern:', error);
      throw error;
    }
  }
}

export default PredictionService;